/**
 * Concept Pages — SEO-optimized explainers for key trading research concepts.
 */
import type { DocPage } from '../types';

export const conceptsDocs: DocPage[] = [
  {
    id: 'concepts/walk-forward-analysis',
    title: 'What Is Walk-Forward Analysis?',
    category: 'getting-started',
    subcategory: 'concepts',
    tags: ['walk-forward analysis', 'wfa', 'out-of-sample', 'validation', 'rolling windows', 'concept'],
    version: '2.0',
    related: ['strategy-optimization/walk-forward-analysis', 'concepts/overfitting', 'concepts/parameter-stability', 'getting-started/research-process'],
    order: 10,
    description: 'Walk-Forward Analysis explained — what it is, why it matters, and how it prevents overfitting',
    content: `# What Is Walk-Forward Analysis?

Walk-Forward Analysis (WFA) is a validation method that tests whether optimized strategy parameters work on data the optimizer has never seen.

It is the most reliable way to distinguish between a strategy that has genuine predictive value and one that has been [overfitted](/docs/concepts/overfitting) to historical data.

## The Problem WFA Solves

When you optimize a strategy, you find the parameters that performed best on a specific dataset. But this creates a circular problem: how do you know those parameters will work on **future** data?

The answer is — you test them on data they were not trained on.

## How It Works

WFA divides your historical data into rolling segments:

\`\`\`
Window 1:  [=== In-Sample ===][-- Out-of-Sample --]
Window 2:       [=== In-Sample ===][-- Out-of-Sample --]
Window 3:            [=== In-Sample ===][-- Out-of-Sample --]
\`\`\`

For each window:

1. **In-sample:** The optimizer searches for the best parameters
2. **Out-of-sample:** The strategy runs with those parameters on unseen data
3. **Roll forward:** The window shifts and the process repeats

The out-of-sample results across all windows are combined. This aggregate is the truest measure of strategy performance.

## Why Out-of-Sample Matters

In-sample results always look good — the optimizer found parameters that fit the data. The question is whether those results generalize.

| Scenario | In-Sample | Out-of-Sample | Interpretation |
|----------|-----------|---------------|----------------|
| Genuine edge | Profitable | Profitable | Strategy has predictive value |
| Overfitted | Very profitable | Unprofitable | Parameters only fit historical noise |
| Weak idea | Marginally profitable | Flat or negative | Not enough signal to exploit |

## Key Metrics

When evaluating WFA results, focus on:

- **Aggregate out-of-sample return** — The combined performance of all forward tests
- **Consistency** — Do most windows produce positive results, or just a few?
- **Parameter stability** — Does the optimizer pick [similar parameters](/docs/concepts/parameter-stability) each time?
- **In-sample / out-of-sample ratio** — If forward tests capture a reasonable fraction of in-sample gains, the strategy is robust

## Common Mistakes

1. **Using too few windows** — More windows give more out-of-sample tests, increasing confidence
2. **Small out-of-sample periods** — Too short and results are dominated by noise
3. **Ignoring parameter drift** — If the optimizer picks wildly different parameters each window, the strategy is fragile
4. **Only counting aggregate return** — A high total return from one big winning window is misleading

## WFA in Quanthop

Quanthop automates the full WFA process:

- Configure window sizes and step intervals
- Run optimization on each in-sample segment
- Collect out-of-sample results automatically
- View window-by-window breakdown and aggregate metrics
- Save results for later comparison

See [Walk-Forward Analysis](/docs/strategy-optimization/walk-forward-analysis) for the step-by-step guide.

## Related

- [Strategy Overfitting Explained](/docs/concepts/overfitting) — What overfitting is and how to detect it
- [Parameter Stability](/docs/concepts/parameter-stability) — Why stable parameters matter
- [The Research Process](/docs/getting-started/research-process) — Where WFA fits in the research workflow
`,
  },
  {
    id: 'concepts/overfitting',
    title: 'Strategy Overfitting Explained',
    category: 'getting-started',
    subcategory: 'concepts',
    tags: ['overfitting', 'curve fitting', 'backtest', 'bias', 'validation', 'concept'],
    version: '2.0',
    related: ['concepts/walk-forward-analysis', 'concepts/parameter-stability', 'strategy-optimization/overview', 'getting-started/research-process'],
    order: 11,
    description: 'What strategy overfitting is, why most backtests lie, and how to detect it',
    content: `# Strategy Overfitting Explained

Overfitting is the most common reason strategies fail in live trading. A strategy is overfitted when it has been tuned to match historical data so closely that it captures noise rather than genuine market patterns.

## What Overfitting Looks Like

An overfitted strategy typically shows:

- Exceptional backtest returns (often 100%+ per year)
- Very high win rates (above 80-90%)
- Sharp peaks in the [optimization surface](/docs/strategy-optimization/reading-optimization-results) — one parameter set works, everything else fails
- Complete failure when tested on new data

## Why It Happens

Overfitting occurs because historical data contains both **signal** (real patterns that persist) and **noise** (random fluctuations that do not repeat).

With enough parameters and enough tuning, any strategy can be made to fit the noise in a specific dataset. The resulting backtest looks exceptional — but the noise it captured will not appear again.

\`\`\`
          Backtest Period            Live Trading
          +--------------+          +--------------+
Signal:   | ~~~~~~~~~~~~ |   same   | ~~~~~~~~~~~~ |
          |              |  ------> |              |
Noise:    | ^^^v^^^v^^^v | different| *..*..*..*.. |
          +--------------+          +--------------+
\`\`\`

The signal persists. The noise is different. An overfitted strategy captured the noise.

## How to Detect Overfitting

### 1. Check Parameter Sensitivity

If small changes to parameters cause large changes in performance, the strategy is fitted to specific values rather than capturing a broad pattern.

Quanthop's [plateau detection](/docs/strategy-optimization/reading-optimization-results) identifies this automatically. A broad plateau of similar results is robust. A sharp peak is a red flag.

### 2. Run Walk-Forward Analysis

[Walk-Forward Analysis](/docs/concepts/walk-forward-analysis) is the most reliable overfitting detector. If optimization results do not hold up out-of-sample, the strategy is overfitted.

### 3. Test on Different Assets

A genuinely robust strategy should show some positive results across related markets. If it only works on one specific symbol and time period, it has likely captured data-specific noise.

### 4. Count the Trades

A strategy with very few trades (under 30) does not have statistical significance. The apparent performance could easily be random chance.

### 5. Question Exceptional Results

If a backtest shows dramatically better results than realistic benchmarks (200%+ annual return with no drawdowns), it is almost certainly overfitted.

## Prevention Strategies

| Method | How It Helps |
|--------|-------------|
| [Walk-Forward Analysis](/docs/strategy-optimization/walk-forward-analysis) | Tests on data the optimizer never saw |
| [Parameter stability analysis](/docs/concepts/parameter-stability) | Ensures the strategy works across a range of values |
| Cross-asset testing | Verifies the idea generalizes beyond one market |
| Sufficient trade count | Requires statistical significance |
| Realistic expectations | Rejects results that are too good to be true |

## The Quanthop Approach

Quanthop treats overfitting as the primary risk in strategy development. Every tool in the [research pipeline](/docs/getting-started/research-process) is designed to filter out overfitted strategies:

- Optimization finds stable regions, not peak values
- WFA validates on unseen data
- Stability scoring measures parameter robustness
- Adaptive Flow catches degradation after deployment

Most strategies that enter the pipeline will be rejected. That is the system working correctly.

## Related

- [What Is Walk-Forward Analysis?](/docs/concepts/walk-forward-analysis) — Out-of-sample validation explained
- [Parameter Stability](/docs/concepts/parameter-stability) — What makes parameters robust
- [The Research Process](/docs/getting-started/research-process) — The complete research workflow
`,
  },
  {
    id: 'concepts/parameter-stability',
    title: 'Parameter Stability Explained',
    category: 'getting-started',
    subcategory: 'concepts',
    tags: ['parameter stability', 'plateau', 'robust', 'optimization', 'sensitivity', 'concept'],
    version: '2.0',
    related: ['concepts/overfitting', 'concepts/walk-forward-analysis', 'strategy-optimization/reading-optimization-results', 'params/optimization'],
    order: 12,
    description: 'What parameter stability means for trading strategies and how to measure it',
    content: `# Parameter Stability Explained

Parameter stability measures how sensitive a strategy's performance is to changes in its parameter values. A stable strategy produces similar results across a range of parameter values. An unstable strategy only works with one exact configuration.

## Why Stability Matters

When you deploy a strategy, the exact market conditions that produced your backtest results will not repeat. If the strategy only works with parameter values of exactly 14 and 0.5, it will likely fail because the optimal values will have shifted.

But if the strategy performs well with any period between 10-20 and any threshold between 0.3-0.7, it is much more likely to remain effective — because there is a **broad region** of parameters that work, not a single fragile point.

## Plateaus vs. Peaks

In the optimization surface:

\`\`\`
Performance
  ^
  |        *          <-- Sharp peak: fragile
  |       / \\
  |      /   \\
  |     /     \\
  |    /       \\
  |___/         \\____
  +--------------------> Parameter Value

Performance
  ^
  |  _______________
  | /               \\   <-- Broad plateau: robust
  |/                 \\
  |                   \\
  |                    \\
  |                     \\
  +--------------------> Parameter Value
\`\`\`

- **Sharp peaks** indicate [overfitting](/docs/concepts/overfitting). The strategy is exploiting a specific data quirk, not a real pattern.
- **Broad plateaus** indicate genuine signal. The strategy captures something that persists across parameter variations.

## How Quanthop Measures Stability

Quanthop's [optimization results](/docs/strategy-optimization/reading-optimization-results) include stability metrics for each parameter cluster:

| Metric | What It Measures |
|--------|-----------------|
| **Stability score** | How consistent results are within the parameter region |
| **Plateau width** | How many parameter combinations produce similar results |
| **Edge sensitivity** | How quickly performance drops at the region boundaries |

Higher stability scores indicate more robust parameter regions.

## Practical Guidelines

### Choosing Parameters

When selecting parameters from optimization results:

1. Pick from the **center** of a plateau, not the edge
2. Prefer clusters with **high stability scores** even if returns are slightly lower
3. Verify with [Walk-Forward Analysis](/docs/concepts/walk-forward-analysis) that the optimizer finds the same region across different time periods

### Warning Signs

- The top result has dramatically higher returns than the second-best result
- Changing one parameter by a single step halves the performance
- [WFA](/docs/strategy-optimization/walk-forward-analysis) picks completely different parameters in each window
- The plateau is very narrow (only 2-3 parameter combinations)

### Good Signs

- Top 10 results all produce similar returns and Sharpe ratios
- A wide range of parameter values gives positive results
- WFA consistently finds parameters in the same region
- The strategy works on multiple assets with similar parameter ranges

## Related

- [Strategy Overfitting Explained](/docs/concepts/overfitting) — Why most backtests lie
- [What Is Walk-Forward Analysis?](/docs/concepts/walk-forward-analysis) — Testing on unseen data
- [Reading Optimization Results](/docs/strategy-optimization/reading-optimization-results) — Understanding stability scores
- [The Research Process](/docs/getting-started/research-process) — Where stability analysis fits
`,
  },
  {
    id: 'concepts/baseline-vs-optimization',
    title: 'Baseline Testing vs. Optimization',
    category: 'getting-started',
    subcategory: 'concepts',
    tags: ['baseline', 'optimization', 'comparison', 'testing', 'concept'],
    version: '2.0',
    related: ['backtesting/running-a-backtest', 'strategy-optimization/overview', 'concepts/overfitting', 'getting-started/research-process'],
    order: 13,
    description: 'Understanding the difference between baseline testing and optimization in strategy research',
    content: `# Baseline Testing vs. Optimization

These are two distinct stages in the [research process](/docs/getting-started/research-process) that serve different purposes. Understanding when to use each, and what to expect from each, is essential for sound strategy development.

## Baseline Testing

A baseline test is a single [backtest](/docs/backtesting/running-a-backtest) with default or reasonable parameter values. Its purpose is to answer one question: **does this strategy idea have any merit at all?**

### What Baseline Testing Shows

- Whether the strategy generates trades
- Whether the direction of results is positive
- Whether the equity curve has any structure (not random)
- Whether the idea is worth spending time optimizing

### What Baseline Testing Does NOT Show

- The best possible performance (parameters are not optimized)
- Whether the strategy is robust (only one configuration was tested)
- Whether it will work in live trading (no out-of-sample validation)

## Optimization

[Optimization](/docs/strategy-optimization/overview) systematically searches the parameter space to find regions that produce good, stable results.

### What Optimization Shows

- Which parameter regions produce consistent results
- How sensitive the strategy is to parameter changes
- Whether [stable plateaus](/docs/concepts/parameter-stability) exist (robustness indicator)
- The upper bound of realistic performance

### What Optimization Does NOT Show

- Whether those parameters will work on future data (use [WFA](/docs/concepts/walk-forward-analysis) for that)
- Whether the strategy has a genuine edge or is just [overfitted](/docs/concepts/overfitting)

## When to Use Each

| Stage | When | Purpose |
|-------|------|---------|
| Baseline | First test of a new idea | Quick sanity check |
| Optimization | After baseline shows promise | Systematic parameter exploration |
| WFA | After optimization finds stable regions | Out-of-sample validation |

## The Critical Difference

Baseline testing is cheap and fast. If the idea clearly fails at baseline, skip optimization — you will save significant time.

Optimization is more expensive. It explores hundreds or thousands of parameter combinations. Only invest this time on ideas that pass baseline testing.

## Anti-Pattern: Skipping Baseline

A common mistake is jumping straight to optimization. This wastes time and increases the risk of finding a false positive — the optimizer may find one narrow parameter set that looks profitable purely by chance.

Always start with a baseline.

## Related

- [Running a Backtest](/docs/backtesting/running-a-backtest) — How to run a baseline test
- [Running Optimization](/docs/strategy-optimization/running-optimization) — How to optimize parameters
- [The Research Process](/docs/getting-started/research-process) — The complete workflow
`,
  },
];
